import { useEffect } from 'react';

/**
 * The per-tab "How this works" drawer.
 *
 * CLONED FROM `views/opportunities-form.ejs` lines 1–119 — the drawer markup, its six
 * panels and the script that keeps the panel in step with the active tab. The copy is
 * reproduced word for word, including the emphasis, the "required" pills and the two
 * cross-module links; CLAUDE.md §1.2 forbids rewriting source copy during a port.
 *
 * The source's `ohShow()` swaps `display` on six always-rendered panels and retitles the
 * header from the `OH_TABS` map. Here the active tab is a prop, so React renders the one
 * panel and derives the same title — same behaviour, no DOM poking.
 *
 * The two links in the Financial panel point at Value & ROI and the NPV Value Model. In
 * the source those are `/value-roi/...` and `/npv-value-model/...`; under the platform
 * they carry the module prefix. That is the routing rewrite §1.2 permits, and the only
 * change made to any of this text.
 */

export type OppTabKey =
  | 'overview'
  | 'ownership'
  | 'business'
  | 'financial'
  | 'documents'
  | 'notes'
  | 'scoring';

/** Source :105 — `OH_TABS`. Note these are the drawer's own labels, which differ from the
 *  tab strip's ("Business" vs "Business Value"). Both are the source's; neither is fixed. */
const OH_TABS: Record<OppTabKey, { icon: string; name: string }> = {
  overview: { icon: '🧭', name: 'Overview' },
  ownership: { icon: '👤', name: 'Ownership' },
  business: { icon: '📊', name: 'Business' },
  financial: { icon: '💰', name: 'Financial' },
  documents: { icon: '📎', name: 'Documents' },
  notes: { icon: '📝', name: 'Notes' },
  /* The view screen's seventh tab. It has no panel in the source drawer, because the
     drawer lives on the EDIT view and that view has no Scoring tab; the copy below is
     therefore the one piece of help text here that is not the source's, and it describes
     the source's own behaviour rather than adding any. */
  scoring: { icon: '📊', name: 'Scoring Details' },
};

const REQ = <span className="oh-req">required</span>;

function Panel({ tab }: { tab: OppTabKey }) {
  switch (tab) {
    /* source :11–23 */
    case 'overview':
      return (
        <>
          <p className="oh-purpose">
            Capture what the opportunity <em>is</em> and how it&rsquo;s classified. This is the
            only tab with required fields.
          </p>
          <div className="oh-lbl">What you do here</div>
          <ul className="oh-list">
            <li>
              <strong>Opportunity ID</strong> {REQ} — any unique code; it must not already exist
              (you&rsquo;ll get a red banner if it does).
            </li>
            <li>
              <strong>Description</strong> {REQ} — a clear one-line summary (max 300 characters).
            </li>
            <li>
              <strong>Relevance for Scoring</strong> {REQ} — Yes/No. Only <em>relevant</em>{' '}
              opportunities can be submitted to scoring.
            </li>
            <li>
              <strong>Opportunity Budget</strong> — optional; when set it{' '}
              <strong>rolls up into the assigned project&rsquo;s total budget</strong>.
            </li>
            <li>
              <strong>Start / End Date</strong> — optional; if they run past the project&rsquo;s
              end date, the <strong>project end date auto-extends</strong> to cover the
              opportunity.
            </li>
            <li>
              <strong>Project</strong> — optional; assign now or later. A{' '}
              <strong>closed (Complete) project will not accept</strong> new opportunities.
            </li>
            <li>
              <strong>Process Hierarchy (L1 → L2 → L3)</strong> — optional; anchors the
              opportunity for portfolio analysis.
            </li>
          </ul>
          <p className="oh-note">
            Saving creates a <strong>Draft</strong>. Set status to <em>Approved</em>, then{' '}
            <strong>Submit to Score</strong> when ready.
          </p>
        </>
      );

    /* source :24–33 */
    case 'ownership':
      return (
        <>
          <p className="oh-purpose">
            Record who <em>owns</em> and sponsors the opportunity, and how critical the underlying
            process is.
          </p>
          <div className="oh-lbl">What you do here</div>
          <ul className="oh-list">
            <li>
              <strong>Opportunity Owner</strong> {REQ} — the person accountable for it.
            </li>
            <li>
              <strong>Owner email</strong> and <strong>BPO email</strong> — optional; used only for
              notifications.
            </li>
            <li>
              <strong>Sponsor / Key Stakeholder</strong> and{' '}
              <strong>Business Process Owner</strong> — optional context.
            </li>
            <li>
              <strong>Business Criticality</strong> — optional; how critical the process this
              opportunity touches is.
            </li>
          </ul>
          <p className="oh-note">
            Only the Owner is mandatory here — the rest can be added later.
          </p>
        </>
      );

    /* source :34–42 */
    case 'business':
      return (
        <>
          <p className="oh-purpose">
            The qualitative <strong>business assessment</strong> dimensions that feed the readiness
            score.
          </p>
          <div className="oh-lbl">What you do here</div>
          <ul className="oh-list">
            <li>
              <strong>AI Strategic Alignment</strong>, <strong>Benefit / Cost Effectiveness</strong>
              , <strong>Delivery Risk</strong> — pick the level that fits.{' '}
              <em>Click a selected value again to clear it.</em>
            </li>
            <li>
              <strong>Business Benefit</strong> — the expected value in currency.
            </li>
          </ul>
          <p className="oh-note">
            All optional at creation — these are normally completed during{' '}
            <strong>scoring</strong>.
          </p>
        </>
      );

    /* source :43–57 */
    case 'financial':
      return (
        <>
          <p className="oh-purpose">
            Financial signals used for prioritization. Two of these are usually{' '}
            <strong>calculated for you in other modules</strong>.
          </p>
          <div className="oh-lbl">What you do here</div>
          <ul className="oh-list">
            <li>
              <strong>Estimated Delivery Cost</strong> — optional manual input.
            </li>
            <li>
              <strong>MVP Value Strength</strong> — you can set it here, but it is normally{' '}
              <strong>auto-calculated from the Value &amp; ROI model</strong>. Build that model
              under <strong>Value &amp; ROI</strong> (Configuration, Cost &amp; Scenarios,
              Financial Summary, Version Comparison); it reflects ROI %, payback, on-prem vs cloud
              scenarios, sensitivity analysis and the number of versions.
            </li>
            <li>
              <strong>NPV Value Strength</strong> — likewise editable here, but normally{' '}
              <strong>auto-calculated from the NPV Value Model</strong>. Build scenarios under{' '}
              <strong>NPV Value Model</strong> (Scenario Setup, NPV Cost &amp; Scenarios, NPV
              Calculator, Scenario Comparison).
            </li>
          </ul>
          <p className="oh-note">
            When a value is <strong>auto-calculated</strong> it&rsquo;s flagged as such and
            reflects the latest model — update the model in those modules and it flows back here
            automatically.
          </p>
          <div className="oh-links">
            <a
              className="oh-link"
              href="/portfolio_definition/financial-modeling/value-roi/configuration"
            >
              Open Value &amp; ROI →
            </a>
            <a
              className="oh-link"
              href="/portfolio_definition/financial-modeling/mvp-value-model/scenario-setup"
            >
              Open NPV Value Model →
            </a>
          </div>
        </>
      );

    /* source :58–65 */
    case 'documents':
      return (
        <>
          <p className="oh-purpose">
            Link supporting documents so the opportunity carries its evidence trail.
          </p>
          <div className="oh-lbl">What you do here</div>
          <ul className="oh-list">
            <li>
              <strong>Initial Discussion</strong>, <strong>Business Requirements</strong>,{' '}
              <strong>Technical Requirements</strong> — paste document URLs (e.g. Google Drive /
              SharePoint).
            </li>
          </ul>
          <p className="oh-note">
            All optional — add links as they become available. These stay editable even in late
            stages.
          </p>
        </>
      );

    /* source :66–74 */
    case 'notes':
      return (
        <>
          <p className="oh-purpose">
            Freeform context and a running commentary of decisions and updates.
          </p>
          <div className="oh-lbl">What you do here</div>
          <ul className="oh-list">
            <li>
              Add <strong>notes / comments</strong> over time to record decisions, blockers and
              changes.
            </li>
          </ul>
          <p className="oh-note">
            Notes can be added even when viewing an opportunity in read-only mode.
          </p>
        </>
      );

    /* Not in the source drawer — see the note on OH_TABS.scoring. */
    case 'scoring':
      return (
        <>
          <p className="oh-purpose">
            The result of scoring this opportunity against its framework. Everything here is{' '}
            <strong>read-only</strong> — it is produced by the scoring screens, not entered
            here.
          </p>
          <div className="oh-lbl">What you see here</div>
          <ul className="oh-list">
            <li>
              <strong>Dimension Scores</strong> — the average per dimension, out of 5, with a
              bar showing where it sits.
            </li>
            <li>
              <strong>Detailed Scoring Responses</strong> — every question answered, its
              component, its weightage and the answer given. Hidden while scoring is still in
              progress.
            </li>
            <li>
              <strong>Detailed Recommendations</strong> — the guidance for the band the overall
              score fell into. Shown once the opportunity is <em>Scored</em>.
            </li>
          </ul>
          <p className="oh-note">
            The tab appears only once there is something to show. To change an answer, reopen
            the opportunity from the scoring queue.
          </p>
        </>
      );
  }
}

export function OpportunityFormHelp({
  tab,
  open,
  onClose,
}: {
  tab: OppTabKey;
  open: boolean;
  onClose: () => void;
}) {
  // Source :117 — Escape closes the drawer.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const meta = OH_TABS[tab];

  return (
    <>
      <div className={`oh-overlay${open ? ' on' : ''}`} onClick={onClose} />
      <aside
        className={`oh-drawer${open ? ' on' : ''}`}
        role="dialog"
        aria-label="How this works"
        aria-hidden={!open}
      >
        <div className="oh-head">
          <div className="oh-title">
            <span className="oh-ic">{meta.icon}</span>
            <span>How the {meta.name} tab works</span>
          </div>
          <button className="oh-x" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>
        <div className="oh-tabhint">
          Help for the <strong>{meta.name}</strong> tab — switch tabs to see each one&rsquo;s
          guide.
        </div>
        <div className="oh-body">
          <Panel tab={tab} />
        </div>
        <div className="oh-foot">
          <a className="oh-link" href="/portfolio_definition/help/user-guide">
            Full guide: how the platform works →
          </a>
        </div>
      </aside>
    </>
  );
}
